import React, { useContext, useState } from 'react';
import { View, Pressable, Switch } from 'react-native';
import { useMutation } from '@apollo/client';

import ClientContext from 'app/src/contexts/ClientContext';
import RelayTable from 'app/src/elements/Table/RelayTable';
import { Text, Title, Setting, SettingLabel } from 'app/src/styles';
import colors from 'app/src/styles/colors';
import { Actions, Action, ActionText } from 'app/src/styles/table';
import { delimited, dateShort } from 'app/src/utils/formatting';

import NewMake from './new';
import EditMake from './edit';
import UnknownMakes from './UnknownMakes';
import { MAKES, UPDATE_MAKE_HIDDEN } from './queries';

const baseColumns = [
  {
    key: 'code',
    label: 'Code',
  },
  {
    key: 'label',
    label: 'Label',
    sort: 'asc',
  },
  {
    key: 'primary',
    label: 'Primary',
    value: ({ value }) => (value ? 'Y' : ''),
  },
  {
    key: 'source',
    label: 'Source',
  },
  {
    key: 'usage',
    label: 'Usage',
    content: delimited,
  },
  {
    key: 'addedAt',
    label: 'Added',
    content: dateShort,
  },
  {
    key: 'hiddenAt',
    label: 'Hidden',
    hidden: true,
    content: dateShort,
  },
  {
    key: 'notes',
    label: 'Notes',
  },
];

const Makes = () => {
  const { hasPolicy } = useContext(ClientContext);
  const [scopes, setScopes] = useState([]);
  const [showNew, setShowNew] = useState();
  const [showEdit, setShowEdit] = useState();
  const [refetch, setRefetch] = useState();

  const columns = [...baseColumns];

  const [updateMakeHidden] = useMutation(UPDATE_MAKE_HIDDEN);

  const confirmUpdateHidden = make => {
    const confirmation =
      `Really ${make.hiddenAt ? 'unhide' : 'hide'} make ${make.label}?`;

    if (window.confirm(confirmation)) {
      updateMakeHidden({
        variables: { id: make.id, hidden: !make.hiddenAt },
        onCompleted: () => refetch(),
      });
    }
  };

  if (hasPolicy({ type: 'Make', action: 'update' })) {
    columns.push({
      key: 'actions',
      label: 'Actions',
      sort: 'prevent',
      content: ({ entry }) => (
        <Actions style={{ flexWrap: 'no-wrap' }}>
          <Action first>
            <Pressable onPress={() => setShowEdit(entry)}>
              <ActionText>Edit</ActionText>
            </Pressable>
          </Action>

          <Action>
            <Pressable onPress={() => setShowNew(entry)}>
              <ActionText>Replace</ActionText>
            </Pressable>
          </Action>

          {hasPolicy({ type: 'Make', action: 'update_hidden' }) && (
            <Action>
              <Pressable onPress={() => confirmUpdateHidden(entry)}>
                <ActionText>{entry.hiddenAt ? 'Unhide' : 'Hide'}</ActionText>
              </Pressable>
            </Action>
          )}
        </Actions>
      )
    });
  }

  columns[columns.length - 2].hidden = !scopes.includes('includeHidden');

  return (
    <View>
      <Title>Makes</Title>

      {hasPolicy({ type: 'Make', action: 'create' }) && (
        <UnknownMakes refetch={refetch} />
      )}

      <Pressable onPress={() => setShowNew({})}>
        <Text>Add Make</Text>
      </Pressable>

      <Setting>
        <SettingLabel>Include Hidden?</SettingLabel>
        <Switch
          value={scopes.includes('includeHidden')}
          onValueChange={value => setScopes(value ? ['includeHidden'] : [])}
          activeTrackColor={colors.lightButton}
          activeThumbColor={colors.button}
        />
      </Setting>

      <RelayTable
        query={MAKES}
        queryName="makes"
        queryVariables={{ scopes }}
        columns={columns}
        setRefetch={setRefetch}
      />

      {showNew && (
        <NewMake
          setShow={setShowNew}
          refetch={refetch}
          replace={showNew?.id ? showNew : null}
        />
      )}

      {showEdit && (
        <EditMake make={showEdit} setShow={setShowEdit} refetch={refetch} />
      )}
    </View>
  );
};

export default Makes;
